import { forwardRef, memo, useCallback, useId, useRef } from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { cva, VariantProps } from 'class-variance-authority';
import { CheckSmallIcon, MinusIcon } from '@page/icons';

import { cn } from '../../lib/styles';
import { Label } from '../Label';

const buttonCheckboxVariant = cn(
  'group py-2 px-2.5 rounded-sm cursor-pointer [&_*]:cursor-pointer',
  'bg-bg-inter-secondary-default hover:bg-bg-inter-secondary-hover active:bg-bg-inter-secondary-pressed',
  'data-[disabled=true]:bg-bg-inter-secondary-disabled data-[disabled=true]:hover:bg-bg-inter-secondary-disabled data-[disabled=true]:active:bg-bg-inter-secondary-disabled',
);

const checkboxVariants = cva('', {
  variants: {
    variant: {
      default: '',
      secondary: buttonCheckboxVariant,
      button: buttonCheckboxVariant,
      tertiary: cn(
        'group py-2 px-2.5 rounded-sm cursor-pointer [&_*]:cursor-pointer',
        'bg-transparent hover:bg-bg-inter-tertiary-hover enabled:active:bg-bg-inter-tertiary-pressed',
        'data-[disabled=true]:bg-transparent data-[disabled=true]:hover:bg-transparent data-[disabled=true]:active:bg-transparent',
      ),
    },
    size: {
      sm: '',
      md: 'py-2.5 px-2.5',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

export interface CheckboxProps
  extends Omit<React.ComponentProps<typeof CheckboxPrimitive.Root>, 'onClick'>,
    VariantProps<typeof checkboxVariants> {
  indeterminate?: boolean;
  label?: React.ReactNode;
  description?: React.ReactNode;
  labelClassName?: string;
  rightContent?: React.ReactNode;
  checkboxClassName?: string;
  alwaysShowError?: boolean;
  error?: React.ReactNode;
  readonly?: boolean;
  /** When true, aligns the control to the top of multi-line labels instead of vertically centering. */
  alignLabelStart?: boolean;
  onCheckedChange?: (checked: boolean, indeterminate?: boolean) => void;
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
}

const CheckboxBase = (
  {
    id: idFromProps,
    className,
    variant,
    label,
    description,
    labelClassName,
    rightContent,
    checked,
    indeterminate,
    disabled,
    alwaysShowError = false,
    error,
    checkboxClassName,
    size,
    readonly,
    alignLabelStart = false,
    onCheckedChange,
    onClick,
    ...rest
  }: CheckboxProps,
  ref: React.ForwardedRef<HTMLButtonElement>,
) => {
  const randomId = useId();
  const id = idFromProps ?? randomId;
  const isProcessing = useRef(false);

  const processsCheckedChange = useCallback(
    (newChecked: boolean) => {
      isProcessing.current = true;

      onCheckedChange?.(newChecked, indeterminate);

      Promise.resolve().then(() => {
        isProcessing.current = false;
      });
    },
    [onCheckedChange, indeterminate],
  );

  const handleCheckedChange = useCallback(
    (newChecked: boolean) => {
      if (readonly || disabled || isProcessing.current) return;

      processsCheckedChange(newChecked);
    },
    [processsCheckedChange, readonly, disabled],
  );

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      onClick?.(event);
      if (readonly || disabled || isProcessing.current) return;

      // Don't handle clicks that originated from the checkbox button or label
      // These are already handled by Radix UI's built-in handlers
      const target = event.target as HTMLElement;
      if (target.closest('[data-slot="checkbox"]') || target.closest('label') || target.tagName === 'LABEL') {
        return;
      }

      processsCheckedChange(!checked);
    },
    [onClick, readonly, disabled, processsCheckedChange, checked],
  );

  const checkboxButton = (
    <CheckboxPrimitive.Root
      ref={ref}
      id={id}
      data-slot="checkbox"
      className={cn(
        'group peer rounded-[4px] border',
        'size-4 shrink-0 transition-all focus-visible:outline-2',
        {
          'cursor-default': readonly,
          'cursor-pointer enabled:hover:border-bg-inter-primary-active enabled:group-hover:border-bg-inter-primary-active':
            !readonly,
        },
        'disabled:cursor-not-allowed disabled:bg-bg-inter-primary-disabled/35 bg-bg-sur-primary',
        {
          'enabled:border-bg-inter-primary-active enabled:bg-bg-inter-primary-default': indeterminate,
          'border-border-secondary enabled:data-[state=checked]:border-bg-inter-primary-active enabled:data-[state=checked]:bg-bg-inter-primary-default enabled:data-[state=checked]:text-white':
            !indeterminate,
        },
        {
          'pointer-events-none': readonly,
        },
        label ? checkboxClassName : checkboxClassName || className,
      )}
      checked={indeterminate ? undefined : checked}
      disabled={disabled}
      onCheckedChange={handleCheckedChange}
      {...rest}
    >
      {indeterminate ? (
        <div className="flex items-center justify-center group-enabled:text-white group-disabled:text-icon-disabled transition-none relative">
          <MinusIcon className="size-5 absolute" fill="currentColor" />
        </div>
      ) : (
        <CheckboxPrimitive.Indicator
          data-slot="checkbox-indicator"
          className="flex items-center justify-center group-enabled:text-white group-disabled:text-icon-disabled transition-none relative"
        >
          <CheckSmallIcon className="size-5 absolute" fill="currentColor" />
        </CheckboxPrimitive.Indicator>
      )}
    </CheckboxPrimitive.Root>
  );

  if (!label) return checkboxButton;

  const labelContent =
    rightContent != null ? (
      <div className={cn('flex w-full gap-2', alignLabelStart ? 'items-start' : 'items-center justify-between')}>
        <span className={cn('w-full', alignLabelStart ? 'whitespace-normal' : 'truncate')}>{label}</span>
        <span className="shrink-0 font-body-xs text-text-secondary font-normal">{rightContent}</span>
      </div>
    ) : (
      label
    );

  return (
    <div className={cn('flex flex-col w-full', className)} onClick={handleClick}>
      <div
        className={cn('flex gap-2 w-full', checkboxVariants({ variant, size }), alignLabelStart && 'items-start', {
          '**:cursor-not-allowed': disabled,
          'cursor-default **:cursor-default': readonly && !disabled,
        })}
        data-disabled={disabled}
      >
        <div className={cn('flex shrink-0', alignLabelStart ? 'pt-0.5' : 'items-center')}>{checkboxButton}</div>

        <Label
          htmlFor={id}
          className={cn(
            'font-body-md font-semibold w-full min-w-0 block',
            alignLabelStart ? 'whitespace-normal' : 'truncate',
            {
              'text-text-primary': !disabled,
              'text-text-disabled': disabled,
              'flex flex-col items-start gap-0 leading-4!': !!description,
            },
            labelClassName,
          )}
        >
          {description ? <span className="truncate w-full">{labelContent}</span> : labelContent}

          {description ? (
            <span
              className={cn('font-body-xs text-text-tertiary font-normal truncate w-full', {
                'text-text-disabled': disabled,
              })}
            >
              {description}
            </span>
          ) : null}
        </Label>
      </div>

      {alwaysShowError || error ? (
        <div data-slot="error" className="h-5 pt-1 text-text-destructive font-body-sm text-left">
          {error}
        </div>
      ) : null}
    </div>
  );
};

export const Checkbox = memo(forwardRef(CheckboxBase));
